import {
  PropertyAttributeCardComponent,
  PropertyAttributeData
} from '@/components/property-attribute-card/property-attribute-card.component';
import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { DictionaryHelper } from '@/helpers/dictionary.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { NewMapComponent } from '@/modules/map/components/new-map/new-map.component';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { SafeUrl } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import {
  Attribute,
  PropertiesService,
  PropertyAttribute,
  PropertyChildType,
  PropertySale,
  RealProperty,
  SalesManagementService
} from '@eemo/api-client';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Dpe2021Component } from '../../../components/dpe-2021/dpe-2021.component';
import { DpeGesComponent } from '../../../components/dpe-ges/dpe-ges.component';
import { PropertyCardComponent } from '../../../components/property-card/property-card.component';

@Component({
  selector: 'app-property-summary',
  templateUrl: './property-summary.component.html',
  styleUrls: ['./property-summary.component.scss'],
  standalone: true,
  imports: [
    MatExpansionModule,
    NgIf,
    NgFor,
    DpeGesComponent,
    Dpe2021Component,
    PropertyCardComponent,
    MatCardModule,
    RouterModule,
    PropertyAttributeCardComponent,
    NewMapComponent,
    TooltipDirective,
    MatButtonModule,
    MatIconModule
  ]
})
export class PropertySummaryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() prop: RealProperty;
  @Input() fullMode = true;

  subs: Subscription[] = [];

  summaryAttrsDict: Attribute[];
  summaryData: PropertyAttributeData[];
  currentSale: PropertySale;
  mainPhoto: SafeUrl;

  energyValue: number;
  gesValue: number;
  energyClassification: string;
  gesClassification: string;
  dpeDate: string;
  dpeDateError = false;
  displayDpe2021 = false;
  propHasRegistry = false;
  showPresentation = false;
  hasPromotionalText = false;
  hasLinks = false;
  showLinksOnOneRow = false;

  canUpdateProperty = false;

  childrenProp: RealProperty[];
  isOwnedChild = false;

  // used to manage change of the property in the parent component without changing tab
  curPropId: string;

  constructor(
    private propService: PropertiesService,
    public propertyHelper: PropertyHelper,
    public dictCacheService: DictionaryCacheService,
    public dictHelper: DictionaryHelper,
    public saleService: SalesManagementService,
    private userHelper: UserHelper
  ) {}

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.summaryAttrsDict = this.dictCacheService.getAttributesForSummary();
    if (this.prop.onGoingSelling) {
      this.subs.push(
        this.saleService.getSale(this.prop.onGoingSelling.uid).subscribe((s) => {
          this.currentSale = s;
          this.hasPromotionalText = s.promotionalText && s.promotionalText !== '';
          this.hasLinks = s.links?.length > 0;
          this.showPresentation = this.hasPromotionalText || this.hasLinks;
          this.showLinksOnOneRow = s.links?.length < 4;
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  ngOnChanges() {
    // manage content reload for navigation on the same page with a different propId
    if (this.prop.uid !== this.curPropId) {
      this.loadData();
    }
  }

  propIsCertified(): boolean {
    // FIXME always return false to not show the identity card
    // return this.prop.certified;
    return false;
  }

  loadData() {
    this.childrenProp = [];
    this.curPropId = this.prop.uid;
    this.propHasRegistry = this.prop.landRegistry?.identifier != null;
    this.summaryData = [];

    this.summaryData.push({ title: 'Type de bien', value: this.propertyHelper.getTypeName(this.prop.type) });
    if (this.prop.children?.length > 0) {
      this.summaryData.push({ title: 'Nombre de biens liés', value: '' + this.prop.children.length });
    }

    this.subs.push(
      this.propService.getRealPropertyAttributesSummary(this.prop.uid).subscribe((data) => {
        const dataTmp: { [key: string]: PropertyAttribute } = {};
        data.forEach((attr) => {
          if (attr.code === environment.DPE_ATTRIBUTE_DATE) {
            this.dpeDate = attr.value;
          } else if (attr.code === environment.DPE_ENERGY_ATTRIBUTE_CODE) {
            this.energyValue = parseInt(attr.value, 10);
          } else if (attr.code === environment.DPE_GES_ATTRIBUTE_CODE) {
            this.gesValue = parseInt(attr.value, 10);
          } else if (attr.code === environment.DPE_ENERGY_CLASSIFICATION_ATTRIBUTE_CODE) {
            this.energyClassification = attr.value;
          } else if (attr.code === environment.DPE_GES_CLASSIFICATION_ATTRIBUTE_CODE) {
            this.gesClassification = attr.value;
          }

          this.dictCacheService.getAttrDisplayNameFromCode;
          dataTmp[attr.code] = attr;
        });

        if (this.dpeDate) {
          const formats = ['DD-MM-YYYY', 'DD/MM/YYYY', 'DD-MM-YY', 'DD/MM/YY'];
          const parsedDate = moment(this.dpeDate, formats, true);

          if (!parsedDate.isValid()) {
            this.dpeDateError = true;
          } else {
            this.displayDpe2021 = parsedDate.toDate() > new Date('2021-07-01');
          }
        }
        // build the attributes list with the good order
        this.summaryAttrsDict.forEach((attr) => {
          if (dataTmp[attr.code]) {
            this.summaryData.push({
              title: this.dictCacheService.getAttrDisplayNameFromCode(attr.code),
              value: this.dictCacheService.getAttrDisplayValueFromCode(attr.code, dataTmp[attr.code].value)
            });
          }
        });
      })
    );
    /*this.mainPhoto = this.propertyHelper.getEmptyMainPhotoImage();
    this.photoService.getMainPhotoImage(this.prop.uid, '640x360', this.prop.mainPhotoTs).subscribe((photo) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        this.mainPhoto = this.domSanitizer.bypassSecurityTrustUrl(reader.result.toString());
      };
      reader.readAsDataURL(photo);
    });*/
    if (this.prop.children && this.prop.children.length > 0) {
      // load children
      this.prop.children.forEach((c) =>
        this.subs.push(this.propService.getRealProperty(c.uid).subscribe((p) => this.childrenProp.push(p)))
      );
    }
    if (this.prop.parent) {
      this.isOwnedChild = this.prop.childType === PropertyChildType.OWNED;
    }
  }
}
